import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type DataInputDialogFactory<TInitialData, TResultData> = (
	initialData: TInitialData,
	onClose: () => void,
	onSave: (result: TResultData) => void
) => ReactElement | null;

export function useDataInputDialog<TInitialData, TResultData>(
	dialogFactory: DataInputDialogFactory<TInitialData, TResultData>
): [
	element: ReactElement | null,
	open: (initialData: TInitialData) => Promise<TryResult<TResultData>>
] {
	const [state, setState] = useState<State<TInitialData, TResultData>>({
		isOpen: false,
		initialData: undefined
	});

	const closeHandler = useCallback(
		(): void => {
			setState(
				previousState => {
					if (!previousState.isOpen) {
						return previousState;
					}

					previousState.promiseHandlers
						.resolve({ wasSuccessful: false });

					return { isOpen: false };
				}
			);
		},
		[]
	);

	const saveHandler = useCallback(
		(updatedData: TResultData): void => {
			setState(previousState => {
				if (!previousState.isOpen) {
					return previousState;
				}

				previousState.promiseHandlers
					.resolve({
						wasSuccessful: true,
						result: updatedData
					});

				return { isOpen: false };
			});
		},
		[]
	);

	const renderElement = (): ReactElement | null => {
		if (!state.isOpen) {
			return null;
		}

		return dialogFactory(
			state.initialData,
			closeHandler,
			saveHandler
		);
	};

	const open = useCallback(
		(initialData: TInitialData): Promise<TryResult<TResultData>> => {
			return new Promise<TryResult<TResultData>>(
				(resolve, reject) => {
					setState({
						isOpen: true,
						initialData,
						promiseHandlers: {
							resolve,
							reject
						}
					});
				}
			);
		},
		[]
	);

	return [
		renderElement(),
		open
	];
}

type State<TInitialData, TResultData> = {
	isOpen: false;
	initialData?: undefined;
	promiseHandlers?: undefined;
} | {
	isOpen: true;
	initialData: TInitialData;
	promiseHandlers: PromiseHandlers<TryResult<TResultData>>;
};